import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { CirclePlus, Search, X, CircleArrowLeft } from "lucide-react";
import pipaanlogo from "../assets/pipaanlogo.svg";
import userimg from "../assets/user.png";
import closecircle from "../assets/images/icons/close-circle.png";
import verifybadge from "../assets/images/icons/verify.svg";
import Leftsidebar from "../components/sidebar/leftsidebar";
import LeftResponsivesidebar from "../components/sidebar/left-responsive";
import Headmenu from "../components/sidebar/headmenu";
import Adminpanel from "../components/nestedtab/adminpanel";
import ChatScreen from "./chatscreen";
import { useSocket } from "../contexts/SocketContext";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const MAX_GROUP_NAME = 100;
const MAX_GROUP_INFO = 255;
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif"];
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const MOBILE_BREAKPOINT = 800;
// First page of the lists, kept for this tab (sessionStorage is cleared on login/logout) so the page
// renders at once and refreshes in the background
const GROUP_LIST_CACHE_KEY = "groupListCache";
const GROUP_REQUESTS_CACHE_KEY = "groupRequestsCache";

const getToken = () => sessionStorage.getItem("accessToken") || localStorage.getItem("accessToken");
const getStoredUserId = () => Number(sessionStorage.getItem("userID") || localStorage.getItem("userID")) || null;

const readCachedList = (key) => {
  try {
    const value = JSON.parse(sessionStorage.getItem(key) || "null");
    return Array.isArray(value) ? value : null;
  } catch {
    return null;
  }
};

const writeCachedList = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {}
};

const displayName = (user) =>
  [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() || user?.username || "User";

const memberCount = (group) => Number(group?.acceptedMembersCount) || 0;

const uploadImage = async (file) => {
  const { data } = await axios.post(
    `${API_BASE_URL}/aws/generate-presigned-url`,
    { files: [{ fileName: file.name, fileType: file.type }] },
    { headers: { token: getToken() } }
  );
  const target = data?.directory?.[0];
  if (!target?.presignedUrl || !target?.fileUrl) throw new Error("Upload could not be started.");
  await axios.put(target.presignedUrl, file, { headers: { "Content-Type": file.type } });
  return target.fileUrl;
};

const toGroupChat = (group) => ({
  id: group.id,
  name: group.group_name,
  avatar: group.group_image,
  membersCount: group.acceptedMembersCount,
  createdAt: group.created_at,
});

const AvatarStack = ({ members = [] }) => {
  const pictures = members.slice(0, 3);
  if (!pictures.length) return null;
  return (
    <div className="flex -space-x-2 shrink-0">
      {pictures.map((member) => (
        <img
          key={member.id}
          src={member.user?.profile_picture_url || userimg}
          alt={displayName(member.user)}
          title={displayName(member.user)}
          className="w-5 h-5 rounded-full border-2 border-white object-cover"
        />
      ))}
    </div>
  );
};

// The open group: its chat (shared ChatScreen in group mode) or its info panel
const GroupTabs = ({ user, groupData, onGroupDelete, view = "chat", onViewChange }) => {
  const selectedGroup = groupData.find((group) => group.id === user?.id) || user;
  const chat = useMemo(() => (selectedGroup ? toGroupChat(selectedGroup) : null), [selectedGroup]);

  if (!selectedGroup) {
    return <p className="text-center text-gray-500 py-10">This group is no longer available.</p>;
  }

  if (view === "info") {
    return (
      <div className="rounded-2xl bg-[#F3F3F3] p-3">
        <Adminpanel
          key={selectedGroup.id}
          matchedGroup={selectedGroup}
          onGroupDelete={onGroupDelete}
          setActiveTab={(tab) => onViewChange?.(tab === "Groupchat" ? "chat" : "info")}
        />
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-[#00000026] bg-[#F7F7F7]">
      <ChatScreen key={selectedGroup.id} isGroup keepGroupRoom chat={chat} onViewInfo={() => onViewChange?.("info")} />
    </div>
  );
};

const GroupCard = ({ group, active, isAdmin, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative w-full flex items-center gap-3 p-2 mb-3 rounded-xl border text-left transition-colors ${
      active ? "border-[#39B54A] bg-[#F4FBF5] shadow-sm" : "border-[#C9DDB5] bg-white hover:border-[#39B54A]"
    }`}
  >
    {Number(group.unreadCount) > 0 && (
      <span
        className="absolute -top-2 -right-2 bg-[#39B54A] text-white text-[10px] min-w-[20px] h-5 px-1 rounded-full flex items-center justify-center font-bold border-2 border-white"
        title={`${group.unreadCount} unread`}
      >
        {Number(group.unreadCount) > 99 ? "99+" : group.unreadCount}
      </span>
    )}
    <img
      src={group.group_image || userimg}
      alt={group.group_name}
      className="w-[72px] h-[72px] rounded-lg object-cover shrink-0"
    />
    <div className="min-w-0 flex-1">
      <h4 className="text-sm font-semibold text-[#000000] truncate">{group.group_name}</h4>
      <div className="flex items-center gap-2 mt-1">
        <AvatarStack members={group.members} />
        <span className="text-xs text-[#000000]">{memberCount(group)} Members</span>
      </div>
      {isAdmin && (
        <p className="flex items-center gap-1 text-[10px] text-[#095228] font-medium mt-1">
          <img src={verifybadge} alt="" className="!w-4 !h-4" />
          You are the Admin
        </p>
      )}
    </div>
  </button>
);

const RequestCard = ({ group, busy, onAccept, onReject }) => (
  <div className="shrink-0 w-[290px] sm:w-[310px] flex items-center gap-3 p-2 rounded-xl border border-[#9CCB6E] bg-white">
    <img
      src={group.group_image || userimg}
      alt={group.group_name}
      className="w-16 h-[72px] rounded-lg object-cover shrink-0"
    />
    <div className="min-w-0 flex-1">
      <h4 className="text-sm font-semibold text-[#000000] truncate">{group.group_name}</h4>
      {group.is_family_group && <span className="text-[10px] text-[#095228] font-semibold">Family</span>}
      <div className="mt-1">
        <AvatarStack members={group.members} />
      </div>
      <p className="text-[11px] text-[#000000] mt-1">{memberCount(group)} Members</p>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <button
        onClick={onAccept}
        disabled={busy}
        className="bg-[#095228] text-white text-xs font-semibold px-3 py-1.5 rounded-full disabled:opacity-50"
      >
        Accept
      </button>
      <button
        onClick={onReject}
        disabled={busy}
        aria-label="Reject"
        className="w-8 h-8 rounded-full border-2 border-[#BE1F2E] text-[#BE1F2E] flex items-center justify-center disabled:opacity-50"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  </div>
);

const CreateGroupModal = ({ isFamily, onClose, onCreated }) => {
  const label = isFamily ? "Family" : "Group";
  const [name, setName] = useState("");
  const [info, setInfo] = useState("");
  const [image, setImage] = useState("");
  const [cover, setCover] = useState("");
  const [uploading, setUploading] = useState("");
  const [friendQuery, setFriendQuery] = useState("");
  const [friendResults, setFriendResults] = useState([]);
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [showFriends, setShowFriends] = useState(false);
  const [selected, setSelected] = useState([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!showFriends) return;
    let cancelled = false;
    setFriendsLoading(true);
    const term = friendQuery.trim();
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.post(
          `${API_BASE_URL}/user/get-all-users`,
          { filters: { isFriendsOnly: true, ...(term ? { search: term } : {}) }, page: 1, limit: 10 },
          { headers: { token: getToken() } }
        );
        if (!cancelled) setFriendResults(data?.result?.users || []);
      } catch {
        if (!cancelled) setFriendResults([]);
      } finally {
        if (!cancelled) setFriendsLoading(false);
      }
    }, term ? 400 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [friendQuery, showFriends]);

  const availableFriends = friendResults.filter((friend) => !selected.some((s) => s.id === friend.id));

  const handleImage = (setter, field) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    if (!IMAGE_TYPES.includes(file.type)) {
      setError("Please choose a JPG, PNG or GIF image.");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError("Image must be 10 MB or smaller.");
      return;
    }
    setUploading(field);
    try {
      setter(await uploadImage(file));
    } catch (err) {
      setError(err.response?.data?.message || "Failed to upload the image. Please try again.");
    } finally {
      setUploading("");
    }
  };

  const handleCreate = async () => {
    const groupName = name.trim();
    const description = info.trim();
    if (!groupName) {
      setError(`${label} Name is required.`);
      return;
    }
    if (!isFamily && !description) {
      setError(`${label} Info is required.`);
      return;
    }
    if (groupName.length > MAX_GROUP_NAME || description.length > MAX_GROUP_INFO) {
      setError(`${label} Name can have at most ${MAX_GROUP_NAME} characters and Info at most ${MAX_GROUP_INFO}.`);
      return;
    }
    setCreating(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/group/add-group`,
        {
          group_name: groupName,
          description,
          group_image: image || null,
          group_cover: cover || null,
          group_members: selected.map((friend) => friend.id),
          is_family_group: isFamily,
        },
        { headers: { token: getToken() } }
      );
      if (data?.success) {
        onCreated(isFamily);
      } else {
        setError(data?.message || `Failed to create the ${label.toLowerCase()}.`);
      }
    } catch (err) {
      setError(err.response?.data?.message || `An error occurred while creating the ${label.toLowerCase()}.`);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="popup-share z-[99999] viewpost-popup fixed inset-0 flex items-center justify-center bg-[#000] bg-opacity-60 w-full backdrop-blur-md">
      <div className="border-popup bg-white rounded-lg relative max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between">
          <div className="logo-popup flex items-center justify-center space-x-2">
            <img src={pipaanlogo} alt="Nyawoo Logo" className="w-50" />
          </div>
          <button className="close-pop-x relative text-gray-500" onClick={onClose} aria-label="Close">
            <img src={closecircle} alt="" className="w-10 h-10" />
          </button>
        </div>
        <div className="max-w-3xl mx-auto p-4 crt-new-grp">
          <div className="flex flex-col items-center space-y-4">
            <div className="relative w-full md:w-auto pb-[100px]">
              <div className="cover-photo-newgrp relative w-full md:w-[750px] h-[200px] bg-green-100 overflow-hidden">
                {cover ? (
                  <img src={cover} alt="Cover Preview" className="w-full h-full object-cover" />
                ) : (
                  <div className="flex justify-center items-center w-full h-full text-gray-500">
                    {uploading === "cover" ? "Uploading..." : "Upload Cover Photo"}
                  </div>
                )}
                <input
                  type="file"
                  accept={IMAGE_TYPES.join(",")}
                  disabled={!!uploading}
                  className="absolute inset-0 w-full h-auto opacity-0 cursor-pointer"
                  onChange={handleImage(setCover, "cover")}
                />
              </div>
              <div className="absolute top-[130px] left-[30px] w-[150px] h-[150px] bg-green-300 rounded-[42px] overflow-hidden">
                {image ? (
                  <img src={image} alt="Profile Preview" className="w-full h-full object-cover" />
                ) : (
                  <div className="flex justify-center items-center w-full h-full text-gray-500 text-center px-2">
                    {uploading === "image" ? "Uploading..." : "Upload Profile"}
                  </div>
                )}
                <input
                  type="file"
                  accept={IMAGE_TYPES.join(",")}
                  disabled={!!uploading}
                  className="absolute inset-0 w-auto h-auto opacity-0 cursor-pointer"
                  onChange={handleImage(setImage, "image")}
                />
              </div>
            </div>
          </div>

          <div className="mb-4 text-left">
            <label htmlFor="create-group-name" className="block text-[#000000] font-medium mb-2">
              {label} Name
            </label>
            <input
              id="create-group-name"
              type="text"
              maxLength={MAX_GROUP_NAME}
              placeholder={`Enter ${label} Name`}
              className="w-full border border-[#095228] rounded-[12px] p-4 focus:outline-none focus:ring-2 focus:ring-green-500"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="mb-4 text-left">
            <label htmlFor="create-group-info" className="block text-[#000000] font-medium mb-2">
              {label} Info
            </label>
            <textarea
              id="create-group-info"
              maxLength={MAX_GROUP_INFO}
              placeholder={`Enter ${label} Info`}
              className="w-full border border-[#095228] rounded-[12px] p-4 h-24 focus:outline-none focus:ring-2 focus:ring-green-500"
              value={info}
              onChange={(e) => setInfo(e.target.value)}
            />
            <p className="text-right text-[11px] text-gray-500">
              {info.length}/{MAX_GROUP_INFO}
            </p>
          </div>

          <div className="mb-4 text-left">
            <label htmlFor="create-group-invite" className="block text-[#000000] font-medium mb-2">
              Invite Friends
            </label>
            <input
              id="create-group-invite"
              type="text"
              maxLength={100}
              placeholder="Search friends to invite"
              className="w-full border border-[#095228] rounded-[12px] p-4 focus:outline-none focus:ring-2 focus:ring-green-500"
              value={friendQuery}
              onFocus={() => setShowFriends(true)}
              onChange={(e) => {
                setShowFriends(true);
                setFriendQuery(e.target.value);
              }}
            />
            {showFriends && (
              <div className="bg-white border rounded shadow max-h-48 overflow-auto mt-2">
                {friendsLoading ? (
                  <p className="px-4 py-3 text-sm text-gray-500">Loading...</p>
                ) : availableFriends.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-gray-500">
                    {friendQuery.trim() ? "No friends match your search." : "No more friends to invite."}
                  </p>
                ) : (
                  availableFriends.map((friend) => (
                    <div key={friend.id} className="hover:bg-gray-100 px-4 py-2 flex items-center justify-between gap-2">
                      <div className="flex items-center min-w-0">
                        <img
                          src={friend.profile_picture_url || userimg}
                          alt={displayName(friend)}
                          className="w-8 h-8 rounded-full mr-3 object-cover shrink-0"
                        />
                        <div className="min-w-0">
                          <p className="font-medium truncate">{displayName(friend)}</p>
                          <p className="text-sm text-gray-500 truncate">@{friend.username}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setSelected((prev) => [...prev, friend])}
                        className="text-sm bg-[#095228] text-white px-3 py-1 rounded-full hover:bg-green-700 shrink-0"
                      >
                        Add As {label} Member
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {selected.map((friend) => (
            <div key={friend.id} className="flex items-center justify-between gap-3 py-3 border-b border-[#161f2814]">
              <div className="flex items-center min-w-0">
                <img
                  src={friend.profile_picture_url || userimg}
                  alt={displayName(friend)}
                  className="w-12 h-12 rounded-full object-cover shrink-0"
                />
                <div className="pl-4 min-w-0 text-left">
                  <p className="font-semibold truncate">{displayName(friend)}</p>
                  <p className="text-[12px] text-[#747474] truncate">@{friend.username}</p>
                </div>
              </div>
              <button
                className="text-[#095228] text-[12px] bg-[#EBF8ED] rounded-[20px] px-3 py-2 shrink-0"
                onClick={() => setSelected((prev) => prev.filter((f) => f.id !== friend.id))}
              >
                Remove
              </button>
            </div>
          ))}

          {error && <p className="text-sm text-red-600 mt-3">{error}</p>}

          <div className="flex justify-end gap-3 mt-4">
            <button onClick={onClose} className="text-[#095228] text-sm bg-[#EBF8ED] rounded-[20px] px-6 py-2">
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={creating || !!uploading}
              className="bg-[#095228] text-white text-sm rounded-[20px] px-6 py-2 min-w-[140px] disabled:opacity-50"
            >
              {creating ? "Creating..." : `Create ${label}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function FriendsPage() {
  const [activeTab, setActiveTab] = useState("Groups");
  const [groupData, setGroupData] = useState(() => readCachedList(GROUP_LIST_CACHE_KEY) || []);
  const [groupRequests, setGroupRequests] = useState(() => readCachedList(GROUP_REQUESTS_CACHE_KEY) || []);
  const [listLoading, setListLoading] = useState(() => !readCachedList(GROUP_LIST_CACHE_KEY));
  const [listLoaded, setListLoaded] = useState(false);
  const [groupSearch, setGroupSearch] = useState("");
  const [activeUser, setActiveUser] = useState(null);
  const [groupView, setGroupView] = useState("chat");
  const [showTabs, setShowTabs] = useState(true);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);
  const [createModal, setCreateModal] = useState(null);
  const [requestBusyId, setRequestBusyId] = useState(null);
  const [notice, setNotice] = useState("");
  const groupsRequestRef = useRef(0);
  const linkHandledRef = useRef(false);
  const currentUserId = getStoredUserId();
  const { socket, isConnected } = useSocket();
  const visibleGroupId = activeUser && groupView === "chat" ? activeUser.id : null;
  const visibleGroupRef = useRef(visibleGroupId);
  visibleGroupRef.current = visibleGroupId;

  const setGroupUnread = useCallback((groupId, update) => {
    setGroupData((prev) =>
      prev.map((g) => (String(g.id) === String(groupId) ? { ...g, unreadCount: update(Number(g.unreadCount) || 0) } : g))
    );
  }, []);

  // The open chat marks itself read on the server; clear its badge here as well
  useEffect(() => {
    if (visibleGroupId) setGroupUnread(visibleGroupId, () => 0);
  }, [visibleGroupId, setGroupUnread]);

  // Listen to every group in the list so unread counts update for groups that are not open
  const groupIdsKey = groupData.map((g) => g.id).join(",");
  useEffect(() => {
    if (!socket || !isConnected || !groupIdsKey) return;
    groupIdsKey.split(",").forEach((id) => socket.emit("join-group", Number(id)));
  }, [socket, isConnected, groupIdsKey]);

  useEffect(() => {
    if (!socket) return;
    const onReceive = (msg) => {
      if (!msg?.is_group_chat || Number(msg.sender_id) === Number(currentUserId)) return;
      if (String(msg.reciever_id) === String(visibleGroupRef.current)) return;
      setGroupUnread(msg.reciever_id, (count) => count + 1);
    };
    const onRead = ({ reader_id, group_id } = {}) => {
      if (group_id && Number(reader_id) === Number(currentUserId)) setGroupUnread(group_id, () => 0);
    };
    socket.on("receive-message", onReceive);
    socket.on("messages-read", onRead);
    return () => {
      socket.off("receive-message", onReceive);
      socket.off("messages-read", onRead);
    };
  }, [socket, currentUserId, setGroupUnread]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(""), 5000);
    return () => clearTimeout(timer);
  }, [notice]);

  const fetchGroupRequests = useCallback(async () => {
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/group/get-groups`,
        { filters: { myPendingGroup: true }, page: 1, limit: 20 },
        { headers: { token: getToken() } }
      );
      if (data?.success) {
        const requests = (data.result.groups || []).filter((group) => group?.is_deleted !== true);
        setGroupRequests(requests);
        writeCachedList(GROUP_REQUESTS_CACHE_KEY, requests);
      }
    } catch (error) {
      console.error("Error fetching group requests:", error);
    }
  }, []);

  const fetchGroups = useCallback(async (search = "") => {
    const requestId = ++groupsRequestRef.current;
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/group/get-groups`,
        { filters: { myAcceptedGroup: true, search: search.trim() }, page: 1, limit: 50 },
        { headers: { token: getToken() } }
      );
      if (requestId !== groupsRequestRef.current) return;
      if (data?.success) {
        const groups = (data.result.groups || []).filter((group) => !group.is_deleted);
        setGroupData(groups);
        if (!search.trim()) writeCachedList(GROUP_LIST_CACHE_KEY, groups);
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
    } finally {
      if (requestId === groupsRequestRef.current) {
        setListLoading(false);
        setListLoaded(true);
      }
    }
  }, []);

  useEffect(() => {
    fetchGroupRequests();
  }, [fetchGroupRequests]);

  useEffect(() => {
    const timer = setTimeout(() => fetchGroups(groupSearch), groupSearch ? 300 : 0);
    return () => clearTimeout(timer);
  }, [groupSearch, fetchGroups]);

  // /groupsfamily?group=<id> (invite link) opens that group for its members
  useEffect(() => {
    if (!listLoaded || linkHandledRef.current) return;
    const linkedId = Number(new URLSearchParams(window.location.search).get("group"));
    if (!linkedId) return;
    linkHandledRef.current = true;
    window.history.replaceState(null, "", window.location.pathname);
    const group = groupData.find((g) => Number(g.id) === linkedId);
    if (group) {
      setActiveTab(group.is_family_group ? "Family" : "Groups");
      setActiveUser(group);
      setGroupView("chat");
      if (isMobile) setShowTabs(false);
    } else if (groupRequests.some((g) => Number(g.id) === linkedId)) {
      setNotice("You have been invited to this group. Accept the request above to join.");
    } else {
      setNotice("You are not a member of this group. Ask a group admin to add you.");
    }
  }, [listLoaded, groupData, groupRequests, isMobile]);

  const handleGroupAction = async (group, action) => {
    const accepting = action === "accept";
    const confirmMsg = accepting
      ? `Join "${group.group_name}"?`
      : `Reject the invitation to "${group.group_name}"?`;
    if (!window.confirm(confirmMsg)) return;
    if (!currentUserId) {
      setNotice("Please sign in again to answer this request.");
      return;
    }
    setRequestBusyId(group.id);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/group/update-member-status`,
        { group_id: group.id, user_id: currentUserId, status: accepting ? "accepted" : "rejected" },
        { headers: { token: getToken() } }
      );
      if (data?.success) {
        setGroupRequests((prev) => {
          const next = prev.filter((g) => g.id !== group.id);
          writeCachedList(GROUP_REQUESTS_CACHE_KEY, next);
          return next;
        });
        setNotice(accepting ? `You joined "${group.group_name}".` : "Request rejected.");
        if (accepting) fetchGroups(groupSearch);
      } else {
        setNotice(data?.message || "Failed to update the request.");
      }
    } catch (error) {
      setNotice(error.response?.data?.message || "Failed to update the request.");
    } finally {
      setRequestBusyId(null);
    }
  };

  const handleGroupDeleteFromList = (deletedGroupId) => {
    socket?.emit("leave-group", deletedGroupId);
    setGroupData((prev) => {
      const next = prev.filter((group) => group.id !== deletedGroupId);
      writeCachedList(GROUP_LIST_CACHE_KEY, next);
      return next;
    });
    setActiveUser((current) => (current?.id === deletedGroupId ? null : current));
    setGroupView("chat");
    setShowTabs(true);
  };

  const handleCreated = (isFamily) => {
    setCreateModal(null);
    setActiveTab(isFamily ? "Family" : "Groups");
    setActiveUser(null);
    setGroupView("chat");
    setNotice(`${isFamily ? "Family" : "Group"} created successfully!`);
    fetchGroups(groupSearch);
  };

  const switchTab = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setActiveUser(null);
    setGroupView("chat");
    setShowTabs(true);
  };

  const openGroup = (group) => {
    setActiveUser(group);
    setGroupView("chat");
    if (isMobile) setShowTabs(false);
  };

  const term = groupSearch.trim().toLowerCase();
  const matchesSearch = (group) => !term || (group.group_name || "").toLowerCase().includes(term);
  const filteredGroups = groupData.filter((group) => !group.is_deleted && !group.is_family_group && matchesSearch(group));
  const filteredFamily = groupData.filter((group) => !group.is_deleted && group.is_family_group && matchesSearch(group));
  const visibleList = activeTab === "Groups" ? filteredGroups : filteredFamily;
  const entityLabel = activeTab === "Groups" ? "Group" : "Family";
  const isGroupAdmin = (group) =>
    group.myRole === "admin" || (currentUserId != null && Number(group.created_by) === currentUserId);

  return (
    <div className="dashboard w-full min-h-screen flex">
      <div className="h-full menu-bar-left">
        <Leftsidebar />
      </div>
      <main className="flex-1 min-w-0 p-4 md:p-5 md:pl-8 main-body">
        <LeftResponsivesidebar />
        <Headmenu />

        {/* Group Requests */}
        <section className="mt-4">
          <h3 className="flex items-center gap-2 text-xl font-semibold text-[#000000]">
            Group Requests
            <span className="bg-[#095228] text-white text-xs min-w-[24px] h-6 px-1.5 rounded-full flex items-center justify-center font-bold">
              {groupRequests.length}
            </span>
          </h3>
          {groupRequests.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">You have no pending group requests.</p>
          ) : (
            <div className="flex gap-3 overflow-x-auto pt-3 pb-3 [scrollbar-width:thin]">
              {groupRequests.map((group) => (
                <RequestCard
                  key={group.id}
                  group={group}
                  busy={requestBusyId === group.id}
                  onAccept={() => handleGroupAction(group, "accept")}
                  onReject={() => handleGroupAction(group, "reject")}
                />
              ))}
            </div>
          )}
        </section>

        {notice && <p className="mt-2 text-sm text-[#095228] font-medium">{notice}</p>}

        {/* Tabs */}
        <div className="flex flex-wrap items-end justify-between gap-3 mt-4">
          <div className="flex gap-6 sm:gap-10">
            {["Groups", "Family"].map((tab) => {
              const active = activeTab === tab;
              const count = tab === "Groups" ? filteredGroups.length : filteredFamily.length;
              return (
                <button
                  key={tab}
                  onClick={() => switchTab(tab)}
                  className={`flex items-center justify-between gap-4 min-w-[120px] sm:min-w-[160px] pb-2 border-b-2 text-lg sm:text-xl font-semibold ${
                    active ? "border-[#095228] text-[#000000]" : "border-[#B8C7BD] text-[#8A8A8A]"
                  }`}
                >
                  {tab}
                  <span
                    className={`text-xs min-w-[24px] h-6 px-1.5 rounded-full flex items-center justify-center font-bold text-white ${
                      active ? "bg-[#095228]" : "bg-[#9DB8A6]"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCreateModal(activeTab === "Family" ? "family" : "group")}
              className="flex items-center gap-2 bg-[#D9F5F7] border border-[#5BC8D1] text-[#21A1AE] text-sm font-semibold px-4 py-2 rounded-full hover:bg-[#c9eff2]"
            >
              <CirclePlus className="w-5 h-5" />
              Create New {entityLabel}
            </button>
            {activeUser && groupView === "info" && (
              <button
                type="button"
                onClick={() => setGroupView("chat")}
                className="flex items-center gap-1.5 bg-[#095228] text-white text-sm font-semibold px-4 py-2 rounded-full hover:bg-[#0b6631]"
              >
                <CircleArrowLeft className="w-4 h-4" />
                Back {entityLabel}
              </button>
            )}
          </div>
        </div>

        {/* List and open group */}
        <div className="mt-4 flex gap-4 min-w-0">
          {(showTabs || !isMobile) && (
            <div className="w-full md:w-[260px] lg:w-[280px] shrink-0">
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B8B8B8]" />
                <input
                  type="text"
                  maxLength={100}
                  placeholder={`Find Your ${entityLabel}`}
                  className="w-full pl-10 pr-4 py-3 text-sm text-[#000000] placeholder-[#B8B8B8] rounded-xl outline-none focus:ring-2 focus:ring-green-500 bg-[#F0F0F0]"
                  value={groupSearch}
                  onChange={(e) => setGroupSearch(e.target.value)}
                />
              </div>
              <div className="md:max-h-[calc(70vh+40px)] md:overflow-y-auto pr-1">
                {listLoading && visibleList.length === 0 ? (
                  <p className="text-center text-gray-500 py-8 text-sm">Loading...</p>
                ) : visibleList.length === 0 ? (
                  <p className="text-center text-gray-500 py-8 text-sm">
                    {term ? `No ${entityLabel.toLowerCase()} matches your search.` : `No ${entityLabel.toLowerCase()} found.`}
                  </p>
                ) : (
                  visibleList.map((group) => (
                    <GroupCard
                      key={group.id}
                      group={group}
                      active={activeUser?.id === group.id}
                      isAdmin={isGroupAdmin(group)}
                      onClick={() => openGroup(group)}
                    />
                  ))
                )}
              </div>
            </div>
          )}

          {(!isMobile || !showTabs) && (
            <div className="flex-1 min-w-0">
              {isMobile && (
                <button onClick={() => setShowTabs(true)} className="mb-2 text-sm font-medium text-[#095228]">
                  &larr; Back to {activeTab}
                </button>
              )}
              {activeUser ? (
                <GroupTabs
                  user={activeUser}
                  groupData={groupData}
                  onGroupDelete={handleGroupDeleteFromList}
                  view={groupView}
                  onViewChange={setGroupView}
                />
              ) : (
                <div className="hidden md:flex min-h-[420px] items-center justify-center rounded-2xl border border-[#00000026] text-gray-400 text-center px-4">
                  Select a {entityLabel.toLowerCase()} to view its chat.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="md:hidden mt-6">
          <p className="text-[#095228] text-[16px] text-center">
            © 2025 <b>Nyawoo</b>. All Rights Reserved
          </p>
        </div>
      </main>

      {createModal && (
        <CreateGroupModal
          isFamily={createModal === "family"}
          onClose={() => setCreateModal(null)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}
